// use client: `incomeRange` is lifted state — it is reported UP from the
// calculator the user types their income into (calculatorComponent →
// JumbotronPF → onIncomeRangeChange) and then selects which Benefits and
// Eligibility props render. It is not a navigational toggle, so it cannot be
// pre-rendered as two CSS-toggled variants, and the state's producer sits
// deeper in the tree than its consumers.
//
// This file IS the client island for these pages: all five callers
// (MicrositePF, MicrositePFTD, MicrositeCCSP, MicroPFi, MicrositeEBS) are
// already Server Components that pass config down — the pattern working as
// intended.
'use client'

import type { BenefitItemProps } from '@/components/benefits/BenefitItem'
import type { FormButton, RatesChargesItem } from '@/components/pf'
import type { ComponentType, ReactNode } from 'react'
import type { IncomeRange } from './pfMicrositeShared'

import { useState } from 'react'

import Benefits from '@/components/benefits'
import Eligibility from '@/components/eligibility'
import FloatingButton from '@/components/fab/FloatingButton'
import { FormsAndAppendices, RatesCharges } from '@/components/pf'
import { JumbotronPF } from '@/sections'
import LetsConnect from '@/sections/ComingSoon/LetsConnect'

import '@/sections/sections.css'

import type { CalculatorProps } from '@/components/calculator/types'

/** A calculator variant filling the jumbotron's calculator slot. */
type CalculatorSlot = ComponentType<CalculatorProps>

export type { IncomeRange } from './pfMicrositeShared'

export type PfMicrositeConfig = {
  defaultIncomeRange?: IncomeRange
  jumbotron: {
    badgeText: ReactNode
    title: string
    subText: ReactNode
    buttonText?: string
    source?: string
    calculatorComponent: CalculatorSlot
  }
  benefits: {
    heading: string
    tnc?: string
    tncLink?: string
    imageHeight?: string
    desktopImages: string
    mobileImage: string
    dataBelow5k: BenefitItemProps[]
    dataAbove5k: BenefitItemProps[]
  }
  /**
   * Eligibility props precomputed per income range (kept as plain data so the
   * config stays serializable across the Server → Client boundary).
   */
  eligibility: { below5k: Record<string, any>; above5k: Record<string, any> }
  rates: {
    heading?: ReactNode
    image?: string
    items: RatesChargesItem[]
    itemsClassName?: string
  }
  forms: {
    heading?: ReactNode
    buttons: FormButton[]
  }
  letsConnectTitle?: string
}

/**
 * Shared layout for the standard personal-financing microsite pages
 * (PF-i, EBS, CCSP-SD, PF-TD, micro PF-i). Every one of these pages was previously
 * a ~400-line copy of the same skeleton; they now supply only the data via config.
 */
const PfMicrositeTemplate = ({ config }: { config: PfMicrositeConfig }) => {
  const [incomeRange, setIncomeRange] = useState<IncomeRange>(
    config.defaultIncomeRange ?? 'above5k'
  )

  const handleIncomeRangeChange = (range: IncomeRange) => setIncomeRange(range)

  const benefitsConfig = {
    heading: config.benefits.heading,
    tnc: config.benefits.tnc,
    tncLink: config.benefits.tncLink,
    imageHeight: config.benefits.imageHeight,
    desktopImages: config.benefits.desktopImages,
    mobileImage: config.benefits.mobileImage,
    benefitsData:
      incomeRange === 'below5k' ? config.benefits.dataBelow5k : config.benefits.dataAbove5k
  }

  return (
    <div>
      <section
        className="section jumbotron"
        data-aos="zoom-out"
        data-aos-delay="200"
        style={{ paddingBottom: '8vh' }}
      >
        <JumbotronPF
          badgeText={config.jumbotron.badgeText}
          title={config.jumbotron.title}
          subText={config.jumbotron.subText}
          buttonText={config.jumbotron.buttonText}
          source={config.jumbotron.source}
          onIncomeRangeChange={handleIncomeRangeChange}
          calculatorComponent={config.jumbotron.calculatorComponent}
        />
      </section>

      <Benefits {...benefitsConfig} />
      <Eligibility
        {...(incomeRange === 'below5k' ? config.eligibility.below5k : config.eligibility.above5k)}
      />
      <RatesCharges
        heading={config.rates.heading}
        image={config.rates.image}
        items={config.rates.items}
        itemsClassName={config.rates.itemsClassName}
      />
      <FormsAndAppendices heading={config.forms.heading} buttons={config.forms.buttons} />

      <LetsConnect title={config.letsConnectTitle} />
      <FloatingButton />
    </div>
  )
}

export default PfMicrositeTemplate
